// src/encounter/lib/wearable-trends.ts
// The patient's device trends for the encounter's chart column, as the encounter owner ruled (30 Oct 2026). Pure: the
// hook reads, this shapes. Rules carried here, so no screen can drop them:
//   - one line per device AND per method: two devices never share a line, SDNN and RMSSD never share a line;
//   - labels and codes (encounter owner sign-off list, 1 Nov): "Resting heart rate (daily)", the device's DAILY value
//     (LOINC 40443-4), never an average of 8867-4 samples; "HRV (SDNN)" 80404-7; "HRV (RMSSD)" with no LOINC, never a
//     borrowed one; "Sleep duration, estimated by the device";
//   - every series carries its device, the MEASUREMENT date of each value and of the latest one, and the source line;
//   - a calendar-spaced axis: one slot per day in the window, a day without a value stays null (gaps stay gaps);
//   - the day is the CLINIC'S calendar day (Asia/Dubai, as for lab dates; encounter owner sign-off M1, 1 Nov): the
//     server sends UTC without an offset, so a UAE daily summary starting at local midnight (20:00Z the day before)
//     belongs to the next day; a night of sleep belongs to the day it ENDED (the wake day);
//   - no judgement: no ranges, flags, colours or interpretation are computed here, and none may be added downstream.
import { clinicWallClockStamp, zonedInstant } from '@/shared/lib/clinic-time';
import type { WearableConnectionWire, WearableDailyWire, WearableSleepWire } from '@/types/wearable-api';

export const WEARABLE_WINDOW_DAYS = 30;
export const WEARABLE_SOURCE_LINE = "From the patient's device, not a clinic measurement";
export const WEARABLE_EMPTY_LINE = 'No device data in the last 30 days';

export type WearableMetric = 'restingHeartRate' | 'hrvSdnn' | 'hrvRmssd' | 'sleepDuration';

interface MetricDef {
  label: string;
  unit: string;
  /** LOINC when one exists for exactly this method; null means none, and none is ever borrowed. */
  loinc: string | null;
  qualifier?: string;
}

export const WEARABLE_METRICS: Readonly<Record<WearableMetric, MetricDef>> = {
  restingHeartRate: { label: 'Resting heart rate (daily)', unit: 'beats/min', loinc: '40443-4' },
  hrvSdnn: { label: 'HRV (SDNN)', unit: 'ms', loinc: '80404-7' },
  hrvRmssd: { label: 'HRV (RMSSD)', unit: 'ms', loinc: null },
  sleepDuration: { label: 'Sleep duration, estimated by the device', unit: 'h', loinc: null },
};

const ORDER: readonly WearableMetric[] = ['restingHeartRate', 'hrvSdnn', 'hrvRmssd', 'sleepDuration'];

export interface WearablePoint {
  /** The clinic's calendar day of the measurement (YYYY-MM-DD): a daily summary's start, a night's end. */
  date: string;
  value: number | null;
}

export interface WearableSeries {
  key: string;
  metric: WearableMetric;
  label: string;
  qualifier?: string;
  unit: string;
  loinc: string | null;
  device: string;
  source: typeof WEARABLE_SOURCE_LINE;
  /** One point per calendar day of the window, oldest first; null where the device sent nothing. */
  points: WearablePoint[];
  /** The latest day with a value, and that value. */
  latest: WearablePoint & { value: number };
  /** When the device last delivered data (connection), shown next to the measurement date, never instead of it. */
  lastSyncAt: string | null;
  /** False for a device the patient has since disconnected: its readings in the window are still real measurements. */
  connected: boolean;
}

export type WearableTrends =
  | { kind: 'hidden' } // no consent or no connection: the section does not render
  | { kind: 'empty'; devices: string[] } // consented and connected, nothing in the window
  | { kind: 'ready'; series: WearableSeries[] };

/**
 * The clinic's calendar day (YYYY-MM-DD) of a server time. A time without an offset is UTC (wearable-api.ts); a time
 * with one is taken as given. Null when it is not a real date-time.
 */
export function clinicDayOf(iso: string): string | null {
  const zoned = /(Z|[+-]\d{2}:?\d{2})$/i.test(iso) ? iso : `${iso}Z`;
  const instant = zonedInstant(zoned);
  return instant === undefined ? null : clinicWallClockStamp(new Date(instant)).slice(0, 10);
}

/** Today on the clinic's calendar (YYYY-MM-DD). */
export const clinicToday = (now: Date = new Date()) => clinicWallClockStamp(now).slice(0, 10);

/** The window's calendar days, oldest first, ending on `today` (YYYY-MM-DD). */
export function windowDays(today: string, days = WEARABLE_WINDOW_DAYS): string[] {
  const end = Date.UTC(+today.slice(0, 4), +today.slice(5, 7) - 1, +today.slice(8, 10));
  return Array.from({ length: days }, (_, i) => new Date(end - (days - 1 - i) * 86_400_000).toISOString().slice(0, 10));
}

const finite = (v: number | null | undefined): v is number => typeof v === 'number' && Number.isFinite(v);

function readings(metric: WearableMetric, daily: WearableDailyWire[], sleep: WearableSleepWire[]) {
  switch (metric) {
    case 'restingHeartRate':
      return daily.map((d) => ({ provider: d.provider, at: d.startTime, received: d.receivedAt, value: d.restingHeartRateBpm }));
    case 'hrvSdnn':
      return daily.map((d) => ({ provider: d.provider, at: d.startTime, received: d.receivedAt, value: d.avgHrvSdnn }));
    case 'hrvRmssd':
      return daily.map((d) => ({ provider: d.provider, at: d.startTime, received: d.receivedAt, value: d.avgHrvRmssd }));
    case 'sleepDuration':
      // The main sleep only: a nap is a different measurement and never adds into the night. Dated by its END (wake day).
      return sleep
        .filter((s) => s.isNap !== true)
        .map((s) => ({ provider: s.provider, at: s.endTime, received: s.receivedAt, value: finite(s.durationAsleepSeconds) ? s.durationAsleepSeconds / 3600 : null }));
  }
}

export function buildWearableTrends(input: {
  connections: WearableConnectionWire[];
  daily: WearableDailyWire[];
  sleep: WearableSleepWire[];
  today: string;
}): WearableTrends {
  const active = input.connections.filter((c) => !c.disconnectedAt);
  if (active.length === 0) return { kind: 'hidden' };

  const days = windowDays(input.today);
  const inWindow = new Set(days);
  // The newest sync per device across its connections: a reconnected device can return its old connection too.
  const lastSync = new Map<string, string | null>();
  for (const c of input.connections) {
    const held = lastSync.get(c.provider) ?? null;
    if (c.lastDataReceivedAt && (!held || c.lastDataReceivedAt > held)) lastSync.set(c.provider, c.lastDataReceivedAt);
    else if (!lastSync.has(c.provider)) lastSync.set(c.provider, held);
  }
  const connected = new Set(active.map((c) => c.provider));
  const series: WearableSeries[] = [];

  for (const metric of ORDER) {
    const def = WEARABLE_METRICS[metric];
    const byDevice = new Map<string, Map<string, { at: string; received: string; value: number }>>();
    for (const r of readings(metric, input.daily, input.sleep)) {
      const day = clinicDayOf(r.at);
      if (!finite(r.value) || !day || !inWindow.has(day)) continue;
      const values = byDevice.get(r.provider) ?? new Map<string, { at: string; received: string; value: number }>();
      // Two rows for one device and day: the later measurement wins; for the same measurement time, the later delivery
      // (a provider's revised summary) wins. Compared, never by array order; values are never averaged together.
      const held = values.get(day);
      const received = r.received ?? '';
      if (!held || r.at > held.at || (r.at === held.at && received > held.received)) values.set(day, { at: r.at, received, value: r.value });
      byDevice.set(r.provider, values);
    }
    for (const [device, values] of [...byDevice].sort(([a], [b]) => a.localeCompare(b))) {
      const points = days.map((date) => ({ date, value: values.get(date)?.value ?? null }));
      const latestDate = [...values.keys()].sort().at(-1)!;
      series.push({
        key: `${metric}:${device}`,
        metric,
        label: def.label,
        qualifier: def.qualifier,
        unit: def.unit,
        loinc: def.loinc,
        device,
        source: WEARABLE_SOURCE_LINE,
        points,
        latest: { date: latestDate, value: values.get(latestDate)!.value },
        lastSyncAt: lastSync.get(device) ?? null,
        connected: connected.has(device),
      });
    }
  }

  return series.length ? { kind: 'ready', series } : { kind: 'empty', devices: active.map((c) => c.provider) };
}
